/**
 * "Send payment-method setup email" — staff tool on a membership's Payment
 * Settings.
 *
 * When a parent asks "how do I put a card on file?", staff click one button
 * and the family gets an email with instructions and a link to their own
 * Payment Settings. It is sent on request, never automatically.
 *
 * The button is not offered when there is no email address on file. A second
 * click cannot send a second email: the button locks while the request is in
 * flight and after it succeeds, and the backend refuses a repeat inside its
 * resend window even from another tab or another staff member.
 *
 * Staff-only. The backend enforces that; this component is only rendered for
 * staff (see UpdateCardForm's `canSendSetupEmail`).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Mail } from "lucide-react";
import { Button, Card } from "../ui";
import { useTheme } from "../../context/ThemeContext";
import { getPrimaryText, getSecondaryText, getMutedText } from "../../utils/themeUtils";
import { captureError } from "../../utils/sentry";
import {
  getPaymentMethodSetupEmailStatus,
  sendPaymentMethodSetupEmail,
  type SetupEmailStatus,
} from "../../api/paymentMethodSetupEmailAPI";

interface Props {
  membershipId?: string | null;
  /**
   * How many saved cards + bank accounts the family already has, or null
   * while that is still loading. Shown so staff know before sending.
   */
  savedMethodCount?: number | null;
}

type LoadState = "loading" | "ready" | "unavailable";

const formatWhen = (iso: string | null): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const PaymentMethodSetupEmail: React.FC<Props> = ({ membershipId, savedMethodCount = null }) => {
  const { isDarkMode } = useTheme();
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [status, setStatus] = useState<SetupEmailStatus | null>(null);
  const [sending, setSending] = useState(false);
  const [confirmation, setConfirmation] = useState<string | null>(null);
  // A ref, not state: two clicks in the same tick both read stale state.
  const inFlight = useRef(false);
  const unlockTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleUnlock = useCallback((seconds: number) => {
    if (unlockTimer.current) clearTimeout(unlockTimer.current);
    if (!seconds || seconds <= 0) return;
    unlockTimer.current = setTimeout(() => {
      setStatus((prev) => (prev ? { ...prev, can_send: prev.has_email, retry_after_seconds: 0 } : prev));
    }, seconds * 1000);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setConfirmation(null);
    if (!membershipId) {
      setLoadState("unavailable");
      return undefined;
    }
    setLoadState("loading");
    getPaymentMethodSetupEmailStatus(localStorage.getItem("authToken"), membershipId)
      .then((res) => {
        if (cancelled) return;
        if (res?.success && res.data) {
          setStatus(res.data);
          setLoadState("ready");
          scheduleUnlock(res.data.retry_after_seconds);
        } else {
          setLoadState("unavailable");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        // 403 = this staff role may not send it. Not an error worth a report.
        if (err?.response?.status !== 403) {
          captureError(err, { component: "PaymentMethodSetupEmail", operation: "status" }, "warning");
        }
        setLoadState("unavailable");
      });
    return () => {
      cancelled = true;
    };
  }, [membershipId, scheduleUnlock]);

  useEffect(
    () => () => {
      if (unlockTimer.current) clearTimeout(unlockTimer.current);
    },
    []
  );

  const handleSend = async () => {
    if (!membershipId || inFlight.current || !status?.can_send) return;
    inFlight.current = true;
    setSending(true);
    try {
      const res = await sendPaymentMethodSetupEmail(localStorage.getItem("authToken"), membershipId);
      const sentTo = res?.data?.sent_to || status.email || "the family";
      const windowSeconds = (status.resend_window_minutes || 10) * 60;
      setStatus({
        ...status,
        can_send: false,
        last_sent_at: res?.data?.sent_at || new Date().toISOString(),
        retry_after_seconds: windowSeconds,
      });
      scheduleUnlock(windowSeconds);
      setConfirmation(`Setup email sent to ${sentTo}. It is saved in Message History.`);
      toast.success(`Setup email sent to ${sentTo}.`);
    } catch (err: any) {
      const body = err?.response?.data;
      if (body?.code === "RECENTLY_SENT") {
        const retry = Number(body?.data?.retry_after_seconds) || 0;
        setStatus({
          ...status,
          can_send: false,
          last_sent_at: body?.data?.last_sent_at || status.last_sent_at,
          retry_after_seconds: retry,
        });
        scheduleUnlock(retry);
        toast.info(body.error || "A setup email was already sent a moment ago.");
      } else if (body?.code === "NO_EMAIL") {
        setStatus({ ...status, has_email: false, can_send: false, email: null });
        toast.error(body.error || "This membership has no email address on file.");
      } else {
        captureError(err, { component: "PaymentMethodSetupEmail", operation: "send" }, "warning");
        toast.error(body?.error || "The setup email could not be sent. Please try again.");
      }
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  };

  if (loadState === "loading") return null;

  const heading = (
    <h3 className={`text-base font-semibold ${getPrimaryText(isDarkMode)}`}>
      Help this family add a payment method
    </h3>
  );

  if (loadState === "unavailable" || !status) {
    return (
      <Card padding="sm" className="mb-6" data-testid="setup-email-card">
        {heading}
        <p className={`text-sm mt-1 ${getMutedText(isDarkMode)}`}>
          The setup email is not available right now.
        </p>
      </Card>
    );
  }

  const lastSent = formatWhen(status.last_sent_at);
  const hasSaved = typeof savedMethodCount === "number" && savedMethodCount > 0;

  return (
    <Card padding="sm" className="mb-6" data-testid="setup-email-card">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          {heading}
          <p className={`text-sm mt-1 ${getSecondaryText(isDarkMode)}`}>
            Emails instructions and a link to their own Payment Settings, where they can add a card
            or bank account.
          </p>

          {status.has_email ? (
            <p className={`text-sm mt-1 break-words ${getSecondaryText(isDarkMode)}`}>
              Sends to <span className="font-medium">{status.email}</span>
            </p>
          ) : (
            <p className="text-sm mt-1 text-amber-700 dark:text-amber-300" data-testid="setup-email-no-address">
              There is no email address on file for this membership, so the setup email cannot be
              sent. Add an email address to the membership first.
            </p>
          )}

          {hasSaved && (
            <p className={`text-sm mt-1 ${getSecondaryText(isDarkMode)}`} data-testid="setup-email-has-saved">
              {savedMethodCount === 1
                ? "This family already has 1 saved payment method (listed below)."
                : `This family already has ${savedMethodCount} saved payment methods (listed below).`}
            </p>
          )}

          {lastSent && !confirmation && (
            <p className={`text-xs mt-1 ${getMutedText(isDarkMode)}`}>Last sent {lastSent}</p>
          )}
          {confirmation && (
            <p role="status" className="text-sm mt-1 text-green-700 dark:text-green-400">
              {confirmation}
            </p>
          )}
          {status.has_email && !status.can_send && !sending && !confirmation && (
            <p className={`text-xs mt-1 ${getMutedText(isDarkMode)}`}>
              One was just sent. You can send another in a few minutes.
            </p>
          )}
        </div>

        {status.has_email && (
          <Button
            type="button"
            variant="secondary"
            size="md"
            className="flex-shrink-0"
            loading={sending}
            disabled={!status.can_send}
            onClick={handleSend}
            icon={<Mail className="w-4 h-4" aria-hidden="true" />}
          >
            Send payment-method setup email
          </Button>
        )}
      </div>
    </Card>
  );
};

export default PaymentMethodSetupEmail;
